import { Injectable } from '@angular/core'
import { Action, Selector, State, StateContext } from '@ngxs/store'
import { tap } from 'rxjs/operators'
import { SalariesSummaryService } from '../salaries-summary.service'
import { GetSalariesSummary, ResetSalariesSummary } from './salaries-summary.actions'
import { SalariesSummaryStateModel } from './salaries-summary.model'
import {
  ISalariesCellData,
  ISalariesLocationRow,
  ISalariesPeriod,
  ISalariesPeriodValue,
  ISalariesSummaryResponse,
  ISalariesSummaryTransformed
} from '../../interfaces/salaries-summary.interface'
import { TABLE_CONSTANTS } from '@lla-platform/core/core-util'

@State<SalariesSummaryStateModel>({
  name: 'salariesSummary'
})
@Injectable()
export class SalariesSummaryState {
  constructor(private salariesSummaryService: SalariesSummaryService) {}

  @Selector()
  static salariesSummary(state: SalariesSummaryStateModel): ISalariesSummaryResponse | undefined {
    return state.salariesSummary
  }

  @Selector()
  static transformedData(state: SalariesSummaryStateModel): ISalariesSummaryTransformed | undefined {
    const response = state.salariesSummary
    if (!response) {
      return undefined
    }

    // Periods come directly from the response with year/month metadata
    const periods: ISalariesPeriod[] = response.periods

    // Helper to convert ISalariesPeriodValue to ISalariesCellData
    const toCell = (periodValue: ISalariesPeriodValue | undefined): ISalariesCellData => {
      if (!periodValue) {
        return {
          value: 0,
          isNewBusiness: false,
          isPartialSelection: false,
          hasMissingPayments: false
        }
      }
      return {
        value: periodValue.amount,
        isNewBusiness: periodValue.isNewBusiness,
        isPartialSelection: periodValue.isPartialSelection,
        hasMissingPayments: periodValue.hasMissingPayments
      }
    }

    // Build location rows from the flat locations array
    const locations: ISalariesLocationRow[] = response.locations.map((loc) => {
      const row: ISalariesLocationRow = {
        locationId: loc.locationId,
        locationName: loc.locationName
      }

      // Add period values as ISalariesCellData (use period.name to access values)
      periods.forEach((period) => {
        const periodValue = loc.values[period.name] as ISalariesPeriodValue | undefined
        row[period.name] = toCell(periodValue)
      })

      // Add Total and Monthly Avg columns
      row['Total'] = toCell(loc.values.total)
      row['MonthlyAvg'] = toCell(loc.values.monthlyAvg)

      return row
    })

    // Build grand total row from globalTotals
    const grandTotalRow: ISalariesLocationRow = {
      locationId: TABLE_CONSTANTS.GRAND_TOTAL_ID,
      locationName: 'Grand Total'
    }

    periods.forEach((period) => {
      const periodValue = response.globalTotals[period.name] as ISalariesPeriodValue | undefined
      grandTotalRow[period.name] = toCell(periodValue)
    })
    grandTotalRow['Total'] = toCell(response.globalTotals.total)
    grandTotalRow['MonthlyAvg'] = toCell(response.globalTotals.monthlyAvg)

    return {
      periods,
      locations,
      grandTotalRow,
      timePeriod: response.timePeriod
    }
  }

  @Action(GetSalariesSummary, { cancelUncompleted: true })
  getSalariesSummary(ctx: StateContext<SalariesSummaryStateModel>, { payload }: GetSalariesSummary) {
    ctx.patchState({
      salariesSummary: undefined
    })
    return this.salariesSummaryService.getSalariesSummary(payload).pipe(
      tap((res) => {
        ctx.patchState({
          salariesSummary: res
        })
      })
    )
  }

  @Action(ResetSalariesSummary)
  resetSalariesSummary(ctx: StateContext<SalariesSummaryStateModel>) {
    ctx.patchState({
      salariesSummary: undefined
    })
  }
}
